import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import './PersonnelModal.css';

// ── Photo uploader circle ─────────────────────────────────
function PhotoCircle({ photo, onUpload, size = 88, placeholder = '👤' }) {
  const ref = useRef();
  function handleFile(e) {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = ev => onUpload(ev.target.result);
    r.readAsDataURL(f);
    e.target.value = '';
  }
  return (
    <div className="pers-photo-circle" style={{ width: size, height: size }}
      onClick={() => ref.current.click()} title="Cliquer pour changer la photo">
      {photo
        ? <img src={photo} alt="" />
        : <span className="pers-photo-placeholder">{placeholder}</span>
      }
      <div className="pers-photo-overlay">📷</div>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}

// ── Badge rôle ────────────────────────────────────────────
function RoleBadge({ role }) {
  const map = {
    admin:   { label: 'Administrateur', color: '#FFB800', bg: 'rgba(255,184,0,0.12)'   },
    manager: { label: 'Manager',        color: '#00BFFF', bg: 'rgba(0,191,255,0.12)'   },
    ouvrier: { label: 'Ouvrier',        color: '#00FF88', bg: 'rgba(0,255,136,0.1)'    },
  };
  const s = map[role] || map.ouvrier;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
      color: s.color, background: s.bg, border: `1px solid ${s.color}40`,
      textTransform: 'uppercase', letterSpacing: 1,
    }}>
      {s.label}
    </span>
  );
}

// ── Carte membre (vue publique / consultation) ────────────
function MembreCard({ membre, isAdmin, onEdit }) {
  return (
    <div className="pers-card">
      <div className="pers-card-top">
        <div className="pers-photo-wrap">
          {membre.photo
            ? <img src={membre.photo} alt="" className="pers-card-photo" />
            : <div className="pers-card-placeholder">{membre.role === 'admin' ? '👑' : membre.role === 'manager' ? '👔' : '🔧'}</div>
          }
          <div className="pers-role-dot" style={{
            background: membre.role === 'admin' ? '#FFB800' : membre.role === 'manager' ? '#00BFFF' : '#00FF88'
          }} />
        </div>
        <div className="pers-card-identity">
          <div className="pers-card-name">{membre.name}</div>
          <RoleBadge role={membre.role} />
          {membre.poste && <div className="pers-card-poste">💼 {membre.poste}</div>}
          {membre.email && <div className="pers-card-email">📧 {membre.email}</div>}
          {membre.tel   && <div className="pers-card-tel">📞 {membre.tel}</div>}
        </div>
      </div>
      {membre.bio && (
        <div className="pers-card-bio">
          <div className="pers-bio-label">À propos</div>
          <p>{membre.bio}</p>
        </div>
      )}
      {membre.competences?.length > 0 && (
        <div className="pers-card-skills">
          {membre.competences.map((c, i) => (
            <span key={i} className="pers-skill-tag">{c}</span>
          ))}
        </div>
      )}
      {isAdmin && (
        <button className="pers-edit-btn" onClick={() => onEdit(membre)}>✏ Modifier la fiche</button>
      )}
    </div>
  );
}

// ── Éditeur de fiche membre ───────────────────────────────
function EditeurMembre({ membre, onSave, onCancel }) {
  const [loc, setLoc] = useState({
    ...membre,
    competences: membre.competences || [],
  });
  const [newSkill, setNewSkill] = useState('');

  function addSkill() {
    if (!newSkill.trim()) return;
    setLoc(l => ({ ...l, competences: [...l.competences, newSkill.trim()] }));
    setNewSkill('');
  }
  function removeSkill(i) {
    setLoc(l => ({ ...l, competences: l.competences.filter((_, j) => j !== i) }));
  }

  return (
    <div className="edit-membre-overlay" onClick={onCancel}>
      <div className="edit-membre-card" onClick={e => e.stopPropagation()}>
        <div className="em-head">
          <h3>✏ Fiche Personnel — {loc.name}</h3>
          <button className="mclose" onClick={onCancel}>✕</button>
        </div>
        <div className="em-body">
          {/* Photo */}
          <div className="em-photo-section">
            <PhotoCircle
              photo={loc.photo} size={100}
              placeholder={loc.role === 'admin' ? '👑' : loc.role === 'manager' ? '👔' : '🔧'}
              onUpload={data => setLoc(l => ({ ...l, photo: data }))}
            />
            <div style={{ flex: 1 }}>
              <div className="em-name">{loc.name}</div>
              <RoleBadge role={loc.role} />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                Cliquer sur la photo pour la modifier
              </div>
            </div>
          </div>

          {/* Informations */}
          <div className="em-grid">
            <div className="fg2">
              <label>Poste / Titre</label>
              <input value={loc.poste || ''} onChange={e => setLoc(l => ({ ...l, poste: e.target.value }))}
                placeholder="Ex: Développeur Full Stack, Chef de projet…" />
            </div>
            <div className="fg2">
              <label>Téléphone</label>
              <input value={loc.tel || ''} onChange={e => setLoc(l => ({ ...l, tel: e.target.value }))}
                placeholder="+229 XX XX XX XX" />
            </div>
            <div className="fg2" style={{ gridColumn: '1/-1' }}>
              <label>Biographie / Présentation</label>
              <textarea rows={4} value={loc.bio || ''} onChange={e => setLoc(l => ({ ...l, bio: e.target.value }))}
                placeholder="Décrivez le parcours, les responsabilités, les spécialités de ce membre…" />
            </div>
            <div className="fg2" style={{ gridColumn: '1/-1' }}>
              <label>Domaines de compétence</label>
              <div className="em-skills-list">
                {loc.competences.map((c, i) => (
                  <span key={i} className="em-skill-tag">
                    {c}
                    <button onClick={() => removeSkill(i)}>✕</button>
                  </span>
                ))}
              </div>
              <div className="em-skill-add">
                <input value={newSkill} onChange={e => setNewSkill(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addSkill()}
                  placeholder="Ex: React, Data Science, Photovoltaïque…" />
                <button className="abtn abtn-blue" style={{ fontSize: 12, padding: '6px 14px' }} onClick={addSkill}>
                  + Ajouter
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="abtn abtn-green" onClick={() => onSave(loc)}>✓ Enregistrer</button>
            <button className="abtn abtn-muted" onClick={onCancel}>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────
export default function PersonnelModal({ onClose }) {
  const { data, updateData, currentUser, showToast } = useApp();
  const isAdmin   = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const [editMembre, setEditMembre] = useState(null);
  const [filterRole, setFilterRole] = useState('all');

  if (!isAdmin && !isManager) return null;

  // Admin himself (virtual entry)
  const adminEntry = {
    id: 0, name: 'Administrateur', role: 'admin',
    photo:   data.adminProfile?.photo   || null,
    poste:   data.adminProfile?.poste   || 'Directeur Général',
    bio:     data.adminProfile?.bio     || '',
    tel:     data.adminProfile?.tel     || '',
    email:   data.contact?.email        || '',
    competences: data.adminProfile?.competences || [],
  };

  const membres = [adminEntry, ...data.workers];
  const displayed = filterRole === 'all' ? membres : membres.filter(m => m.role === filterRole);

  function saveMembre(updated) {
    if (updated.id === 0) {
      // Admin profile
      updateData({ adminProfile: { photo: updated.photo, poste: updated.poste, bio: updated.bio, tel: updated.tel, competences: updated.competences } });
    } else {
      updateData({ workers: data.workers.map(w => w.id === updated.id ? { ...w, ...updated } : w) });
    }
    setEditMembre(null);
    showToast('Fiche mise à jour !');
  }

  const totaux = {
    admin:   membres.filter(m => m.role === 'admin').length,
    manager: membres.filter(m => m.role === 'manager').length,
    ouvrier: membres.filter(m => m.role === 'ouvrier').length,
  };

  return (
    <div className="overlay open">
      <div className="modal modal-xl">
        <div className="mhead">
          <div>
            <h2>👥 Panel Personnel &amp; Administration</h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>
              Présentation, photos et compétences de l'équipe KOTOSTECHNOLOGY
            </p>
          </div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>

        {/* Filtres */}
        <div className="pers-filters">
          {[
            ['all',     `Toute l'équipe (${membres.length})`],
            ['admin',   `Admin (${totaux.admin})`],
            ['manager', `Managers (${totaux.manager})`],
            ['ouvrier', `Ouvriers (${totaux.ouvrier})`],
          ].map(([r, l]) => (
            <button key={r} className={'pers-filter-btn' + (filterRole === r ? ' active' : '')} onClick={() => setFilterRole(r)}>
              {l}
            </button>
          ))}
          {isAdmin && (
            <span className="pers-hint">💡 Cliquez sur une carte pour modifier la fiche</span>
          )}
        </div>

        {/* Grille des cartes */}
        <div className="pers-grid">
          {displayed.map(m => (
            <MembreCard
              key={m.id}
              membre={m}
              isAdmin={isAdmin}
              onEdit={setEditMembre}
            />
          ))}
        </div>

        {editMembre && (
          <EditeurMembre
            membre={editMembre}
            onSave={saveMembre}
            onCancel={() => setEditMembre(null)}
          />
        )}
      </div>
    </div>
  );
}
